import React, { useState, useEffect } from "react";
import styles from "./InputDesign.module.css";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import TranslateIcon from '@mui/icons-material/Translate';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { getFirestore, getDoc, doc, setDoc, collection, addDoc, getDocs } from 'firebase/firestore';
import { app } from '../../index';
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

const PagesSidebar = ({
  pages,
  selectedPage,
  setSelectedPage,
  addPage,
  deletePage,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  draggedOverPage,
  toggleStyleEditor,
  showStyle,
  setPages,
  setStyleSettings,
  styleSettings,
}) => {
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importError, setImportError] = useState(null);
  const [showDbDialog, setShowDbDialog] = useState(false);
  const [dbMenuId, setDbMenuId] = useState("");
  const [dbError, setDbError] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveMenuId, setSaveMenuId] = useState("");
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveUid, setSaveUid] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportJson, setExportJson] = useState("");
  const [stories, setStories] = useState([]);
  const [selectedStoryId, setSelectedStoryId] = useState("");
  const [currentStory, setCurrentStory] = useState(null);

  // Charger les stories au montage du composant
  useEffect(() => {
    const loadStories = async () => {
      const db = getFirestore(app);
      const storiesCollection = collection(db, "stories");
      const storiesSnapshot = await getDocs(storiesCollection);
      const storiesList = storiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStories(storiesList);
    };
    loadStories();
  }, []);

  const handleStorySelect = async (event) => {
    const storyId = event.target.value;
    setSelectedStoryId(storyId);

    if (storyId) {
      const db = getFirestore(app);
      const storyDoc = await getDoc(doc(db, "stories", storyId));
      
      if (storyDoc.exists()) {
        const storyData = storyDoc.data();
        setCurrentStory(storyData);
      }
    } else {
      setCurrentStory(null);
    }
  };

  const handleImportClick = () => {
    setShowImportDialog(true);
    setImportError(null);
  };

  const handleImportClose = () => {
    setShowImportDialog(false);
    setImportError(null);
  };

  const loadFonts = (fontSettings) => {
    if (fontSettings.titleFont && fontSettings.titleFont.name && fontSettings.titleFont.location) {
      const fontUrl = fontSettings.titleFont.location;
      const fontName = fontSettings.titleFont.name;

      if (fontUrl.includes('fonts.googleapis.com')) {
        // Pour les polices Google Fonts
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontUrl;
        document.head.appendChild(link);
      } else {
        // Pour les polices locales (.ttf)
        const style = document.createElement('style');
        style.textContent = `
          @font-face {
            font-family: '${fontName}';
            src: url('${fontUrl}') format('truetype');
            font-display: swap;
          }
        `;
        document.head.appendChild(style);

        const font = new FontFace(fontName, `url(${fontUrl})`);
        font.load().then(() => {
          document.fonts.add(font);
        }).catch((error) => {
          console.warn(`La police ${fontName} n'a pas pu être chargée correctement:`, error);
        });
      }
    }

    if (fontSettings.textFont && fontSettings.textFont.name && fontSettings.textFont.location) {
      const fontUrl = fontSettings.textFont.location;
      const fontName = fontSettings.textFont.name;

      if (fontUrl.includes('fonts.googleapis.com')) {
        // Pour les polices Google Fonts
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontUrl;
        document.head.appendChild(link);
      } else {
        // Pour les polices locales (.ttf)
        const style = document.createElement('style');
        style.textContent = `
          @font-face {
            font-family: '${fontName}';
            src: url('${fontUrl}') format('truetype');
            font-display: swap;
          }
        `;
        document.head.appendChild(style);

        const font = new FontFace(fontName, `url(${fontUrl})`);
        font.load().then(() => {
          document.fonts.add(font);
        }).catch((error) => {
          console.warn(`La police ${fontName} n'a pas pu être chargée correctement:`, error);
        });
      }
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Nettoyage du contenu JSON avant parsing
        const cleanedContent = e.target.result
          .replace(/[\u200B-\u200D\uFEFF]/g, '') // Supprime les caractères invisibles
          .replace(/,(\s*[}\]])/g, '$1') // Supprime les virgules finales avant } ou ]
          .replace(/,(\s*})/g, '}') // Supprime les virgules finales avant }
          .replace(/,(\s*\])/g, ']'); // Supprime les virgules finales avant ]

        const jsonContent = JSON.parse(cleanedContent);
        
        // Validation basique de la structure
        if (!jsonContent.pages || !Array.isArray(jsonContent.pages)) {
          throw new Error("Le fichier JSON doit contenir un tableau 'pages'");
        }

        // Validation de chaque page
        jsonContent.pages.forEach((page, index) => {
          if (!page.pageType || !page.pageEntries) {
            throw new Error(`Page ${index + 1} invalide : doit contenir pageType et pageEntries`);
          }
        });

        // Si tout est valide, on met à jour l'état
        setPages(jsonContent.pages);
        
        // Mise à jour des styleSettings si présents
        if (jsonContent.styleSettings) {
          setStyleSettings(jsonContent.styleSettings);
          // Charger les polices après la mise à jour des styleSettings
          loadFonts(jsonContent.styleSettings);
        }
        
        setShowImportDialog(false);
        setImportError(null);
      } catch (error) {
        console.error("Erreur de parsing JSON:", error);
        setImportError(`Erreur de format JSON: ${error.message}`);
      }
    };

    reader.onerror = () => {
      setImportError("Erreur lors de la lecture du fichier");
    };

    reader.readAsText(file);
  };

  const handleDbDialogOpen = () => {
    setShowDbDialog(true);
    setDbMenuId("");
    setDbError(null);
  };

  const handleDbDialogClose = () => {
    setShowDbDialog(false);
    setDbMenuId("");
    setDbError(null);
  };

  const handleDbMenuIdChange = (e) => {
    setDbMenuId(e.target.value);
  };

  const handleDbLoad = async () => {
    setDbError(null);
    if (!dbMenuId) {
      setDbError("Veuillez saisir un menuId.");
      return;
    }
    try {
      const db = getFirestore(app);
      const menuRef = doc(db, "menus", dbMenuId);
      const menuSnap = await getDoc(menuRef);
      if (!menuSnap.exists()) {
        setDbError("Aucun menu trouvé pour cet identifiant.");
        return;
      }
      const menuData = menuSnap.data();
      if (!menuData.menuPages || !Array.isArray(menuData.menuPages)) {
        setDbError("Le menu chargé n'a pas de pages valides.");
        return;
      }
      // menuData.menuPages contient juste les ids des pages du menu
      // on doit récupérer les pages du menu
      const menuPages = await Promise.all(menuData.menuPages.map(async (pageId) => {
        const pageRef = doc(db, "menu_pages", pageId);
        const pageSnap = await getDoc(pageRef);
        const pageData = pageSnap.data();
        // Ajouter l'ID de la page aux données
        return {
          ...pageData,
          id: pageId
        };
      }));
      setPages(menuPages);
      if (menuData) {
        // ne pas mettre dans styleSettings les pages du menu
        const { menuPages, ...menuStyle } = menuData;
        setStyleSettings(menuStyle);
      }
      setShowDbDialog(false);
    } catch (err) {
      setDbError("Erreur lors du chargement : " + err.message);
    }
  };

  const handleSaveDialogOpen = () => {
    setShowSaveDialog(true);
    setSaveMenuId("");
    setSaveError(null);
  };

  const handleSaveDialogClose = () => {
    setShowSaveDialog(false);
    setSaveMenuId("");
    setSaveError(null);
  };

  const handleSaveMenuIdChange = (e) => {
    setSaveMenuId(e.target.value);
  };

  const handleSave = async () => {
    setSaveError(null);
    setIsSaving(true);
    
    if (!saveMenuId) {
      setSaveError("Veuillez saisir un menuId.");
      setIsSaving(false);
      return;
    }

    try {
      const db = getFirestore(app);          

      const pageIds = await Promise.all(pages.map(async (page) => {
        let pageRef;
        
        if (page.id) {        
          pageRef = doc(db, "menu_pages", page.id);
        } else {
          pageRef = doc(collection(db, "menu_pages"));
        }
                        
        const { id, ...pageData } = page;        
        if (!pageData.uid) {
          pageData.uid = currentStory.uid;
        }
        await setDoc(pageRef, pageData);        
        return pageRef.id;
      }));

      const menuRef = doc(db, "menus", saveMenuId);
      // TODO for some reason, menuPages are still present in styleSettings even if we filter them at db load 
      const { menuPages, ...styleSettingsCleaned } = styleSettings;   
      if (!styleSettingsCleaned.uid) {
        styleSettingsCleaned.uid = currentStory.uid;
      }
         
      await setDoc(menuRef, {
        menuPages: pageIds,
        ...styleSettingsCleaned
      });

      setShowSaveDialog(false);
      setSaveMenuId("");
      setSaveError(null);
    } catch (err) {
      setSaveError("Erreur lors de la sauvegarde : " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const translateText = async (texts) => {
    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: "Tu es un traducteur professionnel. Traduis le texte suivant du français vers l'anglais (il s'agit de page(s) d'un menu de restaurant). Retourne uniquement la traduction, sans commentaires ni explications. Si plusieurs textes sont fournis, retourne-les dans le même ordre, séparés par des virgules."
            },
            {
              role: "user",
              content: Array.isArray(texts) ? texts.join(" ||| ") : texts
            }
          ],
          temperature: 0.3
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Erreur API ChatGPT:", errorData);
        throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const translatedText = data.choices[0].message.content.trim();
      console.log("Translation result:", translatedText);
      
      if (Array.isArray(texts)) {
        return translatedText.split(" ||| ");
      }
      return translatedText;
    } catch (error) {
      console.error("Error translating text:", error);
      return Array.isArray(texts) ? texts : texts;
    }
  };

  const translateAllPages = async () => {
    setIsTranslating(true);
    try {
      const newPages = [...pages];
      const textsToTranslate = [];
      const translationMap = new Map(); // Pour stocker les correspondances texte original -> traduction

      // Première passe : collecter tous les textes à traduire
      for (const page of newPages) {
        if (page.pageType.fr && (!page.pageType.en || page.pageType.en.trim() === "")) {
          textsToTranslate.push(page.pageType.fr);
        }

        for (const pageEntry of page.pageEntries) {
          if (pageEntry.text?.fr && (!pageEntry.text.en || pageEntry.text.en.trim() === "")) {
            textsToTranslate.push(pageEntry.text.fr);
          }

          if (pageEntry.entriesTitle?.fr && (!pageEntry.entriesTitle.en || pageEntry.entriesTitle.en.trim() === "")) {
            textsToTranslate.push(pageEntry.entriesTitle.fr);
          }

          if (pageEntry.entries) {
            for (const entry of pageEntry.entries) {
              if (entry.entryTitle?.fr && (!entry.entryTitle.en || entry.entryTitle.en.trim() === "")) {
                textsToTranslate.push(entry.entryTitle.fr);
              }

              if (entry.entrySubtitle?.fr && (!entry.entrySubtitle.en || entry.entrySubtitle.en.trim() === "")) {
                textsToTranslate.push(entry.entrySubtitle.fr);
              }

              if (entry.entryPrice?.fr) {
                for (const price of entry.entryPrice.fr) {
                  if (price.match(/[a-zA-Z]+/) && (!entry.entryPrice.en || !entry.entryPrice.en.includes(price))) {
                    textsToTranslate.push(price);
                  }
                }
              }
            }
          }
        }
      }

      // Traduire tous les textes en une seule fois
      if (textsToTranslate.length > 0) {
        const translations = await translateText(textsToTranslate);

        // Créer une map des traductions
        textsToTranslate.forEach((text, index) => {
          translationMap.set(text, translations[index]);
        });
      }

      // Deuxième passe : appliquer les traductions
      for (const page of newPages) {
        if (page.pageType.fr && (!page.pageType.en || page.pageType.en.trim() === "")) {
          page.pageType.en = translationMap.get(page.pageType.fr);
        }

        for (const pageEntry of page.pageEntries) {
          if (pageEntry.text?.fr && (!pageEntry.text.en || pageEntry.text.en.trim() === "")) {
            pageEntry.text.en = translationMap.get(pageEntry.text.fr);
          }

          if (pageEntry.entriesTitle?.fr && (!pageEntry.entriesTitle.en || pageEntry.entriesTitle.en.trim() === "")) {
            pageEntry.entriesTitle.en = translationMap.get(pageEntry.entriesTitle.fr);
          }

          if (pageEntry.entries) {
            for (const entry of pageEntry.entries) {
              if (entry.entryTitle?.fr && (!entry.entryTitle.en || entry.entryTitle.en.trim() === "")) {
                entry.entryTitle.en = translationMap.get(entry.entryTitle.fr);
              }

              if (entry.entrySubtitle?.fr && (!entry.entrySubtitle.en || entry.entrySubtitle.en.trim() === "")) {
                entry.entrySubtitle.en = translationMap.get(entry.entrySubtitle.fr);
              }

              if (entry.entryPrice?.fr) {
                entry.entryPrice.en = entry.entryPrice.fr.map(price => {
                  if (price.match(/[a-zA-Z]+/)) {
                    return translationMap.get(price) || price;
                  }
                  return price;
                });
              }
            }
          }
        }
      }

      setPages(newPages);
    } catch (error) {
      console.error("Erreur lors de la traduction:", error);
      alert("Une erreur est survenue lors de la traduction");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleExportClick = () => {
    const exportData = {
      pages,
      styleSettings: {
        backgroundColor: styleSettings.backgroundColor,
        closeColor: styleSettings.closeColor,
        detailBackgroundColor: styleSettings.detailBackgroundColor,
        detailCloseColor: styleSettings.detailCloseColor,
        detailDescColor: styleSettings.detailDescColor,
        detailPriceColor: styleSettings.detailPriceColor,
        detailTextColor: styleSettings.detailTextColor,
        priceColor: styleSettings.priceColor,
        textColor: styleSettings.textColor,
        descColor: styleSettings.descColor,
        textFont: styleSettings.textFont,
        titleFont: styleSettings.titleFont,
        titleFontSize: styleSettings.titleFontSize,
      }
    };
    setExportJson(JSON.stringify(exportData, null, 2));
    setShowExportDialog(true);
  };

  const handleExportClose = () => {
    setShowExportDialog(false);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(exportJson);
  };

  return (
    <aside className={styles.sidebar}>
      <div style={{ margin: "0 -24px 0px -24px", padding: "0 24px 16px 24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <FormControl fullWidth style={{ marginBottom: 16 }}>
            <InputLabel>Story</InputLabel>
            <Select
              value={selectedStoryId}
              onChange={handleStorySelect}
              label="Story"
            >
              {stories.map((story) => (
                <MenuItem key={story.id} value={story.id}>
                  {story.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <h2 style={{ fontSize: "24px", fontWeight: "bold", color: "#1d192b" }}>
            Menu Restaurant X
          </h2>
          <div className={styles.flexGap}>
            <button
              onClick={toggleStyleEditor}
              className={styles.primaryButton}
              style={{ width: "fit-content" }}
            >
              Éditer le style
            </button>
            <button onClick={addPage} className={styles.primaryButton}>
              Add a page
            </button>
          </div>
        </div>
      </div>
      <div style={{ margin: "0 -24px 24px -24px", borderBottom: "1px solid #b2ede7" }}></div>
      <div className={styles.flexFlexColGap} style={{ flex: 1, overflowY: "auto" }}>
        {pages.map((page, index) => (
          <div
            key={index}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`${styles.flexJustifyBetweenItemsCenter} ${
              selectedPage === index ? "bg-gray-100" : ""
            } ${draggedOverPage === index ? "border-dashed" : ""}`}
            style={{
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              cursor: "grab",
              backgroundColor:
                selectedPage === index ? "#f3f4f6" : "transparent",
              borderStyle: draggedOverPage === index ? "dashed" : "solid",
              opacity: draggedOverPage === index ? "0.5" : "1",
              transform: draggedOverPage === index ? "scale(1.02)" : "none",
              transition: "all 0.2s ease",
            }}
            onClick={() => setSelectedPage(index)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ cursor: "grab", color: "#9ca3af", userSelect: "none" }}>☰</span>
              <span>
                {page.pageType.fr || page.pageType.en || `Page ${index + 1}`}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deletePage(index);
              }}
              style={{
                backgroundColor: "transparent",
                border: "none",
                color: "#ef4444",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      {/* Boutons en bas de la barre latérale */}
      <div style={{ marginTop: "auto", padding: "16px 0" }}>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleImportClick}
          fullWidth
        >
          Importer JSON
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloudDownloadIcon />}
          style={{ marginTop: 8 }}
          onClick={handleExportClick}
          fullWidth
        >
          Exporter JSON
        </Button>
        <Button
          variant="outlined"
          style={{ marginTop: 8 }}
          onClick={handleDbDialogOpen}
          fullWidth
        >
          Charger depuis la base de données
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          style={{ marginTop: 8 }}
          onClick={handleSaveDialogOpen}
          fullWidth
        >
          Sauvegarder dans la base de données
        </Button>
        <Button
          variant="outlined"
          startIcon={<TranslateIcon />}
          style={{ marginTop: 8 }}
          onClick={translateAllPages}
          disabled={isTranslating}
          fullWidth
        >
          {isTranslating ? "Traduction en cours..." : "Tout traduire"}
        </Button>
      </div>

      {/* Dialog d'import */}
      <Dialog open={showImportDialog} onClose={handleImportClose}>
        <DialogTitle>Importer un menu depuis un fichier JSON</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            style={{ margin: '20px 0' }}
          />
          {importError && (
            <div style={{ color: 'red', marginTop: '10px' }}>
              {importError}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose}>Annuler</Button>
        </DialogActions>
      </Dialog>
      {/* Dialog de chargement depuis la base de données */}
      <Dialog open={showDbDialog} onClose={handleDbDialogClose}>
        <DialogTitle>Charger un menu depuis la base de données</DialogTitle>
        <DialogContent>
          <div style={{ margin: '12px 0' }}>
            <label htmlFor="menuIdInput">Identifiant du menu (menuId) :</label>
            <input
              id="menuIdInput"
              type="text"
              value={dbMenuId}
              onChange={handleDbMenuIdChange}
              style={{ width: '100%', marginTop: 8, padding: 8, borderRadius: 4, border: '1px solid #b2ede7' }}
              placeholder="Exemple : WgpXG6xvvYUdpit8zuK1PDDmXNM2"
            />
            {dbError && <div style={{ color: 'red', marginTop: 8 }}>{dbError}</div>}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDbDialogClose}>Annuler</Button>
          <Button variant="contained" onClick={handleDbLoad}>Charger</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de sauvegarde dans la base de données */}
      <Dialog open={showSaveDialog} onClose={handleSaveDialogClose}>
        <DialogTitle>Sauvegarder le menu dans la base de données</DialogTitle>
        <DialogContent>
          <div style={{ margin: '12px 0' }}>
            <label htmlFor="saveMenuIdInput">Identifiant du menu (menuId) :</label>
            <input
              id="saveMenuIdInput"
              type="text"
              value={saveMenuId}
              onChange={handleSaveMenuIdChange}
              style={{ width: '100%', marginTop: 8, padding: 8, borderRadius: 4, border: '1px solid #b2ede7' }}
              placeholder="Exemple : WgpXG6xvvYUdpit8zuK1PDDmXNM2"
            />
            {saveError && <div style={{ color: 'red', marginTop: 8 }}>{saveError}</div>}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveDialogClose}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'export JSON */}
      <Dialog open={showExportDialog} onClose={handleExportClose} maxWidth="md" fullWidth>
        <DialogTitle>Exporter le menu</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
            {exportJson}
          </pre>
        </DialogContent>
        <DialogActions>
          <button onClick={handleCopyToClipboard} className={styles.primaryButton}>
            Copier
          </button>
          <button onClick={handleExportClose} className={styles.primaryButton}>
            Fermer
          </button>
        </DialogActions>
      </Dialog>
    </aside>
  );
};

export default PagesSidebar;
